import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NewsDetail } from '../@classes/newsDetail';
import { News } from '../@classes/news';
import { Company } from '../@classes/company';
import { Hashtag } from '../@classes/hashtag';
import { Archive } from '../@classes/archive';
import { Constants } from '../@classes/constants';

@Injectable({ providedIn: 'root' })
export class NewsService {
	constructor(
		private apiService: ApiService,
	) { }

	getNews(
		page: number = 1,
		company: Company | null = null,
		archive_year: string | null = null,
		archive_month: string | null = null,
		hashtag: string | null = null,
		news_per_page: number = 5,
		withLoadingScreen: boolean = true
	){
		let params: any = {page: page, news_per_page: news_per_page};
		if(company !== null) {
			params.company_id = company.id;
		}
		if(archive_year !== null) {
			params.archive_year = archive_year;
			params.archive_month = archive_month;
		}
		if(hashtag !== null) {
			params.hashtag = hashtag;
		}
		return this.apiService.get('news/get_news', params, "", withLoadingScreen)
			.pipe(
				map(response => {
					const news: News[] = [];
					response['extras'].news.forEach((item : any) => {
						this.cacheNewsSlug(item['url_title'], item['id']);
						news.push({
							id: item['id'] as number,
							title: item['title'],
							created_at: item['created_at'],
							url_title: item['url_title'],
							image: item['image'],
							hits: item['hits'],
							description: item['description'],
							category: item['category']
						});
					});
					const popular_news: News[] = [];
					response['extras'].popular_news.forEach((item : any) => {
						this.cacheNewsSlug(item['url_title'], item['id']);
						popular_news.push({
							id: item['id'] as number,
							title: item['title'],
							created_at: item['created_at'],
							url_title: item['url_title'],
							image: item['image'],
							hits: item['hits'],
							description: item['description'],
							category: item['category']
						});
					});
					const hashtags: Hashtag[] = [];
					Object.entries(response['extras'].hashtags).forEach(([key, value], index) => {
						hashtags.push({
							name: value as string,
						});
					});
					const archives: Archive[] = [];
					response['extras'].archives.forEach((item : any, index : number )=> {
						archives.push({
							id: index + 1 as number,
							date: item['date'],
							total: item['total'],
						});
					});
					return {
						news: news,
						popular_news: popular_news,
						hashtags: hashtags,
						archives: archives,
						total_news: response['extras'].total_news
					};
				})
			);
	}

	getNewsDetail(title: string, news_id: number) {
		this.cacheNewsSlug(title, news_id);
		
		return this.apiService.get('news/news_detail/' + title + '/' + news_id)
			.pipe(
				map(response => {
					const latest_news: NewsDetail[] = [];
					response['extras'].news_latest.forEach((item : any) => {
						const urlTitle = item['url_title'] ?? item['news_url_title'] ?? item['news_title'];
						this.cacheNewsSlug(urlTitle, item['id_news']);
						latest_news.push({
							company_code: item.company_alt,
							company_id: item.company_id as number,
							company_name: item.company_name,
							id_news: item.id_news as number,
							news_category_id: item.news_category_id as number,
							news_category_title: item.news_category_title,
							category:  item.category,
							news_cdate: item.news_cdate,
							news_udate: item.news_udate,
							news_cuser: item.news_cuser,
							news_hits: item.news_hits,
							news_preambule: item.news_preambule,
							news_text: item.news_text,
							news_title: item.news_title,
							url_title: urlTitle,
							page_category_id: item.page_category_id as number,
							username: item.username,
							image: Constants.BASE_ASSET_URL +"_file/news/thumbs/" + item['id_news'] +".jpg",
							news_tags: null,
							latest_news: null
						});
					});

					return {
						company_code: response['extras']['rs'].company_code,
						company_id: response['extras']['rs'].company_id as number,
						company_name: response['extras']['rs'].company_name,
						id_news: response['extras']['rs'].id_news as number,
						news_category_id: response['extras']['rs'].news_category_id as number,
						news_category_title: response['extras']['rs'].news_category_title,
						category: response['extras']['rs'].news_category_name,
						news_cdate: response['extras']['rs'].news_cdate,
						news_udate: response['extras']['rs'].news_udate,
						news_cuser: response['extras']['rs'].news_cuser,
						news_hits: response['extras']['rs'].news_hits,
						news_preambule: response['extras']['rs'].news_preambule,
						news_text: response['extras']['rs'].news_text,
						news_title: response['extras']['rs'].news_title,
						url_title: response['extras']['rs']['url_title'] ?? response['extras']['rs']['news_url_title'] ?? title,
						page_category_id: response['extras']['rs'].page_category_id as number,
						username: response['extras']['rs'].username,
						image: response['extras']['meta_img'] == '' ? Constants.BASE_ASSET_URL +"_file/news/" + response['extras']['rs']['id_news'] +".jpg" : response['extras']['meta_img'],
						news_tags: response['extras']['news_tags'],
						latest_news: latest_news
					};
				})
			);
	}

	private cacheNewsSlug(title: string, news_id: number) {
		if (typeof window === 'undefined' || !title || !news_id) {
			return;
		}

		const normalizedTitle = this.normalizeNewsSlug(title);
		const cache = this.readSlugCache();
		cache[title] = news_id;
		cache[normalizedTitle] = news_id;
		window.sessionStorage.setItem('news-slug-id-map', JSON.stringify(cache));
	}

	private readSlugCache(): Record<string, number> {
		if (typeof window === 'undefined') {
			return {};
		}

		try {
			const raw = window.sessionStorage.getItem('news-slug-id-map');
			return raw ? JSON.parse(raw) as Record<string, number> : {};
		} catch {
			return {};
		}
	}

	getCachedNewsId(title: string): number | null {
		const cache = this.readSlugCache();
		const cached = cache[title] ?? cache[this.normalizeNewsSlug(title)];
		return typeof cached === 'number' && cached > 0 ? cached : null;
	}

	async resolveNewsIdByTitle(title: string): Promise<number | null> {
		const normalizedTitle = this.normalizeNewsSlug(title);
		const cached = this.getCachedNewsId(title);
		if (cached) {
			return cached;
		}

		const firstPage = await firstValueFrom(
			this.getNews(1, null, null, null, null, 100, false)
		);
		const firstMatch = firstPage.news.find((item) => this.isMatchingNewsSlug(item, normalizedTitle));
		if (firstMatch) {
			this.cacheNewsSlug(firstMatch.url_title || title, firstMatch.id);
			return firstMatch.id;
		}

		const totalPages = Math.max(1, Math.ceil((firstPage.total_news ?? 0) / 100));
		for (let page = 2; page <= totalPages; page++) {
			const result = await firstValueFrom(
				this.getNews(page, null, null, null, null, 100, false)
			);
			const match = result.news.find((item) => this.isMatchingNewsSlug(item, normalizedTitle));
			if (match) {
				this.cacheNewsSlug(match.url_title || title, match.id);
				return match.id;
			}
		}

		return null;
	}

	private isMatchingNewsSlug(item: News, normalizedTitle: string) {
		return this.normalizeNewsSlug(item.url_title) === normalizedTitle
			|| this.normalizeNewsSlug(item.title) === normalizedTitle;
	}

	private normalizeNewsSlug(value: string | null | undefined): string {
		return String(value ?? '')
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '');
	}
}
